import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import { Button } from "@/common/components/ui/button";
import {
  Building2,
  CheckCircle,
  Plus,
  Trash2,
  Smartphone,
  Landmark,
  Shield,
  CreditCard,
  FileText,
} from "lucide-react";

interface BankAccount {
  bankName: string;
  branchName: string;
  accountNumber: string;
}

interface UpiId {
  id: string;
  isVerified: boolean;
}

interface UserData {
  bankAccounts: BankAccount[];
  upiIds: UpiId[];
  pan: string;
  aadhar: string;
  [key: string]: any;
}

interface BankDetailsTabProps {
  userData: UserData;
  setUserData: (data: any) => void;
  readOnly?: boolean;
  validationErrors?: Record<string, string>;
}

const BankDetailsTab = ({
  userData,
  setUserData,
  readOnly = false,
  validationErrors = {},
}: BankDetailsTabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";

  const handleBankChange = (
    index: number,
    field: keyof BankAccount,
    value: string
  ) => {
    const newBankAccounts = [...(userData.bankAccounts || [])];
    newBankAccounts[index] = { ...newBankAccounts[index], [field]: value };
    setUserData({ ...userData, bankAccounts: newBankAccounts });
  };

  const handleAddBank = () => {
    const newBank: BankAccount = {
      bankName: "",
      branchName: "",
      accountNumber: "",
    };
    setUserData({
      ...userData,
      bankAccounts: [...(userData.bankAccounts || []), newBank],
    });
  };

  const handleRemoveBank = (index: number) => {
    const newBankAccounts = [...(userData.bankAccounts || [])];
    newBankAccounts.splice(index, 1);
    setUserData({ ...userData, bankAccounts: newBankAccounts });
  };

  const handleUpiChange = (index: number, field: keyof UpiId, value: any) => {
    const newUpiIds = [...(userData.upiIds || [])];
    newUpiIds[index] = { ...newUpiIds[index], [field]: value };
    setUserData({ ...userData, upiIds: newUpiIds });
  };

  const handleAddUpi = () => {
    const newUpi: UpiId = {
      id: "",
      isVerified: false,
    };
    setUserData({
      ...userData,
      upiIds: [...(userData.upiIds || []), newUpi],
    });
  };

  const handleRemoveUpi = (index: number) => {
    const newUpiIds = [...(userData.upiIds || [])];
    newUpiIds.splice(index, 1);
    setUserData({ ...userData, upiIds: newUpiIds });
  };

  const verifiedBadge = (
    <div className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-emerald-600">
      <CheckCircle className="h-3.5 w-3.5" />
      <span className="text-[10px] font-bold uppercase tracking-wide">
        Verified
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Bank Accounts Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <span className="text-base font-bold text-slate-800">
              Bank Account Details
            </span>
          </div>
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={handleAddBank}
            disabled={readOnly}
            type="button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Bank Account
          </Button>
        </div>

        {userData.bankAccounts?.map((bank, index) => (
          <Card
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-primary/20"
          >
            <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4">
              <CardTitle className="text-sm font-bold text-slate-700">
                Bank Account
              </CardTitle>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() => handleRemoveBank(index)}
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Bank Name
                  </Label>
                  <div className="relative">
                    <Landmark className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={bank.bankName}
                      onChange={e =>
                        handleBankChange(index, "bankName", e.target.value)
                      }
                      className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                      disabled={readOnly}
                      placeholder="e.g. HDFC Bank"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Branch Name
                  </Label>
                  <Input
                    value={bank.branchName}
                    onChange={e =>
                      handleBankChange(index, "branchName", e.target.value)
                    }
                    className="h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10"
                    disabled={readOnly}
                    placeholder="e.g. Mumbai Main Branch"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Account Number
                  </Label>
                  <Input
                    value={bank.accountNumber}
                    onChange={e =>
                      handleBankChange(index, "accountNumber", e.target.value)
                    }
                    className="h-11 border-slate-200 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10"
                    disabled={readOnly}
                    placeholder="e.g. 123456789012"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {(!userData.bankAccounts || userData.bankAccounts.length === 0) && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-slate-500">
            <p>No Bank Accounts Added</p>
          </div>
        )}
      </div>

      {/* UPI IDs Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <span className="text-base font-bold text-slate-800">
              UPI Details
            </span>
          </div>
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/90"
            onClick={handleAddUpi}
            disabled={readOnly}
            type="button"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add UPI ID
          </Button>
        </div>

        {userData.upiIds?.map((upi, index) => (
          <Card
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className="rounded-xl border border-slate-200 bg-white shadow-sm transition-all hover:border-primary/20"
          >
            <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4">
              <CardTitle className="text-sm font-bold text-slate-700">
                UPI ID
              </CardTitle>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() => handleRemoveUpi(index)}
                  type="button"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="grid grid-cols-2 space-y-6 p-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    UPI ID
                  </Label>
                  {verifiedBadge}
                </div>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={upi.id}
                    onChange={e => handleUpiChange(index, "id", e.target.value)}
                    className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                    disabled={readOnly}
                    placeholder="e.g. user@upi"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {(!userData.upiIds || userData.upiIds.length === 0) && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-slate-500">
            <p>No UPI IDs Added</p>
          </div>
        )}
      </div>

      {/* Identity Documents Section */}
      <Card className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-bold text-slate-800">
            <Shield className="h-5 w-5 text-primary" />
            KYC
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-8 gap-y-6 p-6 md:grid-cols-2">
          {/* PAN Card */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                PAN Card Details *
              </Label>
              {userData.pan && verifiedBadge}
            </div>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={userData.pan}
                onChange={e =>
                  setUserData({ ...userData, pan: e.target.value })
                }
                className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.pan
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                disabled={readOnly}
                placeholder="XXXXX0000X"
              />
            </div>
            {validationErrors.pan && (
              <p className="text-xs text-red-600">{validationErrors.pan}</p>
            )}
          </div>

          {/* Aadhar Card */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Aadhaar Details *
              </Label>
              {userData.aadhar && verifiedBadge}
            </div>
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={userData.aadhar}
                onChange={e =>
                  setUserData({ ...userData, aadhar: e.target.value })
                }
                className={`h-11 border-slate-200 pl-10 font-medium transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass} ${
                  validationErrors.aadhar
                    ? "border-red-300 focus:border-red-500"
                    : ""
                }`}
                disabled={readOnly}
                placeholder="0000 0000 0000"
              />
            </div>
            {validationErrors.aadhar && (
              <p className="text-xs text-red-600">{validationErrors.aadhar}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankDetailsTab;
